'use client'

import { useEffect } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
} from '@/Components/ui/select'
import { Locale } from '@/types'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useSafeDirection } from '@/hooks/use-safe-translation'

interface LanguageSwitchProps {
  locales?: Locale[]
}

const defaultLocales: Locale[] = [
  { label: 'English', code: 'en', dir: 'ltr' },
  { label: 'دری', code: 'fa', dir: 'rtl' },
  { label: 'پښتو', code: 'ps', dir: 'rtl' },
]

export default function LanguageSwitch({ locales = defaultLocales }: LanguageSwitchProps) {
  const { i18n } = useTranslation()
  const direction = useSafeDirection()
  useEffect(() => {
    const storedLang = localStorage.getItem('i18nextLng')
    if (storedLang) {
      i18n.changeLanguage(storedLang)
    }
  }, [i18n])

  useEffect(() => {
    const selectedLocale = locales.find((l) => l.code === i18n.language)
    if (selectedLocale) {
      document.documentElement.dir = selectedLocale.dir
      document.documentElement.lang = selectedLocale.code
    }
  }, [i18n.language, locales])

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem('i18nextLng', lang)
  }

  return (
    <Select value={i18n.language} onValueChange={handleLanguageChange}>
      <SelectTrigger
        className="!dark:text-white w-fit rounded-lg border-border font-semibold text-muted-foreground focus-visible:ring-0 supports-[backdrop-filter]:drop-shadow-sm"
        dir={direction}
      >
        <SelectValue placeholder="Select Language" />
      </SelectTrigger>
      <SelectContent className="border-border" dir={direction}>
        <SelectGroup>
          {locales.map((lang) => (
            <SelectItem
              key={lang.code}
              value={lang.code}
              className={cn(
                'font-rtl font-semibold',
                lang.code === i18n.language ? 'text-primary dark:text-primary' : ''
              )}
            >
              {lang.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}
